// Moved from `src/hooks/mutation/useMutateRegenerateDocument.ts` (now a
// delegate) at ARCFE-1004 — the first of the three hooks that import
// `generateDocument`, and the reason the family was blocked on E3.
//
// The unblock is E3's OPTION 2, not the ownership ruling: `generateDocument`
// is injected as a port. Ruling it `@domain session` would not have helped —
// it is a ~350L orchestration engine that dispatches Rematch analytics, reads
// two flags, resolves the capture provider and writes a jotai atom, so it
// breaks the headless rule whatever its tag.
//
// What moved: the cache contract (cancel, snapshot, the documents-list row
// patch, every invalidation, the abort/not-transcribed cache writes) and the
// ERROR CLASSIFICATION — deciding which failure a message represents is data
// logic. What stayed: all five toasts, both quota surfaces, the atom writes
// and every analytics dispatch, fired from ports on a discriminated outcome
// so the delegate never re-parses an error string.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { logger } from '@heidi/runtime/logger';
import { type GenerateTrigger } from '@heidi/runtime/performance';

import {
  GET_DOCUMENTS_BY_ID,
  GET_DOCUMENT_BY_ID,
  StreamingStatus,
  TRANSCRIPT_DELETED_ERROR,
  type GetDocumentsResponse,
  type QueryDocumentResponse,
} from '../../contracts';

export const REGENERATE_DOCUMENT = 'RegenerateDocument';

const isDocumentGenerationInFlight = (status: StreamingStatus | undefined): boolean =>
  status === StreamingStatus.UPLOADING ||
  status === StreamingStatus.AWAITING_STREAMING_START ||
  status === StreamingStatus.STREAMING ||
  status === StreamingStatus.FINISHING_STREAMING;

/** The app's `generateDocument`, injected (E3 option 2). */
export type GenerateDocumentPort = (input: {
  sessionId: string;
  documentId: string;
  brain?: unknown;
  generationMethod?: unknown;
  templateId?: string;
  aiCommandId?: string;
  aiCommandText?: string;
  voiceStyle?: unknown;
  customStyle?: unknown;
  isPersonalizationApplied?: boolean;
  trigger: GenerateTrigger;
  onAbort: (documentBeforeAbort: QueryDocumentResponse | undefined) => void;
}) => Promise<unknown>;

export type RegenerateDocumentInput = {
  documentId: string;
  brain?: unknown;
  templateId?: string | null;
  aiCommandId?: string | null;
  aiCommandText?: string | null;
  generationMethod?: unknown;
  voiceStyle?: unknown;
  customStyle?: unknown;
  isPersonalizationApplied?: boolean;
  /** `explicit` for a user action, `autonomous` for an automatic run. */
  trigger: GenerateTrigger;
};

/**
 * The classified failure. The package owns this mapping so the delegate
 * switches on a shape instead of re-comparing error strings — the strings are
 * a wire contract, and two places parsing them is how they drift.
 */
export type RegenerateDocumentFailure =
  | { kind: 'transcript-deleted' }
  | { kind: 'session-not-transcribed' }
  | { kind: 'stream-manually-aborted' }
  | { kind: 'unlimited-quota-until-unset' }
  | { kind: 'quota-exceeded' }
  | { kind: 'unknown'; message: string; status?: number };

const classify = (error: unknown): RegenerateDocumentFailure | null => {
  if (!(error instanceof Error)) return null;
  switch (error.message) {
    case TRANSCRIPT_DELETED_ERROR:
      return { kind: 'transcript-deleted' };
    case 'SessionNotTranscribedError':
      return { kind: 'session-not-transcribed' };
    case 'StreamManuallyAbortedError':
      return { kind: 'stream-manually-aborted' };
    case 'UnlimitedQuotaUntilNotSetError':
      return { kind: 'unlimited-quota-until-unset' };
    case 'ASKAIQuotaExceedError':
    case 'TemplateQuotaExceedError':
      return { kind: 'quota-exceeded' };
    default: {
      // A rejected stream carries its HTTP status; copy is picked from it.
      const { status } = error as { status?: unknown };
      return {
        kind: 'unknown',
        message: error.message,
        ...(typeof status === 'number' ? { status } : {}),
      };
    }
  }
};

export function useMutateRegenerateDocument({
  sessionId,
  generateDocument,
  notTranscribedContent,
  onGenerationAborted,
  onRegenerated,
  onRegenerateFailed,
  invalidateCurrentUser,
  invalidateGamifyTrialExtension,
  invalidateAllTemplates,
  invalidateClinicalTasks,
}: {
  sessionId: string;
  generateDocument: GenerateDocumentPort;
  /**
   * Copy written into the document cache when the session was never
   * transcribed — intl lives app-side, so the resolved string crosses.
   */
  notTranscribedContent: string;
  /** App-reaction port: the two abort toasts. `restored` distinguishes them. */
  onGenerationAborted?: (input: { restored: boolean }) => void;
  /** App-reaction port: the analytics pair, fired after the cache snapshot. */
  onRegenerated?: (input: {
    documentId: string;
    templateId?: string | null;
    previousGenerationType?: string | null;
    isRegeneration: boolean;
  }) => void;
  /** App-reaction port: toasts, quota surfaces, error analytics. */
  onRegenerateFailed?: (
    failure: RegenerateDocumentFailure,
    input: { documentId: string }
  ) => void;
  invalidateCurrentUser?: () => void;
  invalidateGamifyTrialExtension?: () => void;
  invalidateAllTemplates?: () => Promise<unknown>;
  invalidateClinicalTasks?: (sessionId: string) => Promise<unknown>;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [REGENERATE_DOCUMENT, { sessionId }],
    mutationFn: async (input: RegenerateDocumentInput) => {
      const {
        documentId,
        brain,
        generationMethod,
        templateId,
        aiCommandId,
        aiCommandText,
        voiceStyle,
        customStyle,
        isPersonalizationApplied,
        trigger,
      } = input;
      // Generate document
      const data = await generateDocument({
        sessionId: sessionId,
        documentId: documentId,
        brain: brain,
        generationMethod: generationMethod || undefined,
        templateId: templateId || undefined,
        aiCommandId: aiCommandId || undefined,
        aiCommandText: aiCommandText || undefined,
        voiceStyle: voiceStyle || undefined,
        customStyle: customStyle || undefined,
        isPersonalizationApplied: isPersonalizationApplied || undefined,
        trigger,
        onAbort: (documentBeforeAbort: QueryDocumentResponse | undefined) => {
          if (!documentBeforeAbort) {
            return;
          }
          logger.debug('documentBeforeAbort', documentBeforeAbort);
          // The two abort toasts differ on whether the previous version was
          // restored — decided here because it reads streaming state.
          onGenerationAborted?.({
            restored:
              documentBeforeAbort.streamingStatus ===
                StreamingStatus.AWAITING_STREAMING_START ||
              documentBeforeAbort.streamingStatus === StreamingStatus.NOT_STARTED,
          });
          queryClient.setQueryData<QueryDocumentResponse>(
            [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
            (oldData) => {
              if (!oldData) {
                return undefined;
              }

              return {
                ...oldData,
                content: oldData?.content || documentBeforeAbort?.content,
                streamingStatus: StreamingStatus.MANUALLY_ABORTED,
                abortController: new AbortController(),
              };
            }
          );
        },
      });
      return data;
    },
    onSuccess: async (_, { documentId, templateId }) => {
      await queryClient.cancelQueries({
        queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
      });

      const previousDocument = queryClient.getQueryData<QueryDocumentResponse>([
        GET_DOCUMENT_BY_ID,
        { sessionId, documentId },
      ]);

      invalidateCurrentUser?.();
      invalidateGamifyTrialExtension?.();

      onRegenerated?.({
        documentId,
        templateId,
        previousGenerationType: previousDocument?.generation_type ?? null,
        isRegeneration: !!previousDocument?.content,
      });

      return { previousDocument };
    },
    onSettled: async (_, error, { documentId }) => {
      const documentStreamingStatus = queryClient.getQueryData<QueryDocumentResponse>([
        GET_DOCUMENT_BY_ID,
        { sessionId, documentId },
      ])?.streamingStatus;

      // User Stop lands on MANUALLY_ABORTED. A superseded transport rejects
      // with StreamManuallyAbortedError while the replacement remains active;
      // only that outcome may skip invalidation. Successful generations also
      // settle while their cache still says STREAMING.
      if (
        documentStreamingStatus === StreamingStatus.MANUALLY_ABORTED ||
        (error?.message === 'StreamManuallyAbortedError' &&
          isDocumentGenerationInFlight(documentStreamingStatus))
      ) {
        return;
      }

      const listKey = [GET_DOCUMENTS_BY_ID, { sessionId }] as const;

      const latestDoc = queryClient.getQueryData<QueryDocumentResponse>([
        GET_DOCUMENT_BY_ID,
        { sessionId, documentId },
      ]);

      if (latestDoc) {
        queryClient.setQueryData<GetDocumentsResponse>(
          listKey,
          (old: GetDocumentsResponse | undefined): GetDocumentsResponse | undefined => {
            if (!old) return old;

            const newDocs = old.documents.map((doc) => {
              if (doc.id === documentId) {
                return {
                  ...doc,
                  name: latestDoc.name,
                  generation_type: latestDoc.generation_type ?? doc.generation_type,
                  document_tab_type: latestDoc.document_tab_type ?? doc.document_tab_type,
                };
              }
              return doc;
            });

            return { ...old, documents: newDocs };
          }
        );
      }

      await invalidateAllTemplates?.();

      // A refetch resets GENERATION_FAILED to NOT_STARTED, so a failed run keeps
      // the restored content and its failed state instead.
      if (!(error && documentStreamingStatus === StreamingStatus.GENERATION_FAILED)) {
        await queryClient.invalidateQueries({
          queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
        });
      }

      // Invalidate clinical tasks to reflect backend status changes (e.g., marking done)
      await invalidateClinicalTasks?.(sessionId);

      if (error?.message === 'SessionNotTranscribedError') {
        queryClient.setQueryData<QueryDocumentResponse>(
          [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
          (data) => {
            if (data) {
              return {
                ...data,
                content: notTranscribedContent,
                streamingStatus: StreamingStatus.NOT_STARTED,
              };
            }
            return data;
          }
        );
      }
    },
    onError: (error, { documentId }) => {
      const failure = classify(error);
      if (failure) {
        onRegenerateFailed?.(failure, { documentId });
      }
    },
  });
}
